import { ChatDetailsResponse } from '@choco-admin/types';
import { Button, Chip } from '@heroui/react';
import React, { useState } from 'react';
import { FiEdit, FiMapPin, FiTruck, FiExternalLink } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  EntitySearchModal,
  type EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { FormFooter } from '../../../../../../../components/FormFooter';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useSearchBuyersForChat } from '../../../../../hooks/useSearchBuyersForChat';
import { useSearchBuyerUsers } from '../../../../../hooks/useSearchBuyerUsersForChat';
import { useUpdateChatBuyer } from '../../../../../hooks/useUpdateChatBuyer';
import { useChatPageContext } from '../../../contexts';

interface BuyerMetadata {
  email?: string;
  phone?: string;
  whatsApp?: boolean;
  sms?: boolean;
  fax?: boolean;
  orderPreferences?: { type: string }[];
  rolesEnabled?: boolean;
  fullAddress?: string;
  customerNumberMandatory?: boolean;
  referenceId?: string;
  name?: string;
}

type BuyerEntitySearchOption = EntitySearchOption & {
  metadata?: BuyerMetadata;
};

interface BuyerSectionProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const BuyerSection: React.FC<BuyerSectionProps> = ({
  chatData,
  chatId,
}) => {
  const { chatData: contextChatData } = useChatPageContext();
  const currentChatData = contextChatData || chatData;
  const currentBuyer = currentChatData.buyerEntity;
  const navigate = useNavigate();

  const [isChangeBuyerModalOpen, setIsChangeBuyerModalOpen] = useState(false);
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);

  const { addNotification } = useNotification();

  const {
    buyers,
    isLoading: searchLoading,
    searchBuyers,
  } = useSearchBuyersForChat();
  const { users: buyerUsers, isLoading: usersLoading } = useSearchBuyerUsers({
    buyerId: selectedBuyerId || '',
  });

  const { updateChatBuyer, isUpdating } = useUpdateChatBuyer({
    onSuccess: () => {
      addNotification({
        title: 'Buyer Updated',
        message: 'The chat buyer has been updated successfully.',
        type: 'success',
      });
      setIsChangeBuyerModalOpen(false);
      setSelectedBuyerId(null);
    },
    onError: (error) => {
      addNotification({
        title: 'Update Failed',
        message: error.message,
        type: 'error',
      });
    },
  });

  const handleBuyerSelect = (selectedBuyer: BuyerEntitySearchOption) => {
    setSelectedBuyerId(selectedBuyer.id);
  };

  const handleConfirm = async (selectedBuyer: BuyerEntitySearchOption) => {
    if (!selectedBuyer.id) return;

    await updateChatBuyer({
      chatId,
      buyerId: selectedBuyer.id,
      buyerUserIds: buyerUsers.map((user) => user.value),
    });
  };

  const handleCancel = () => {
    setIsChangeBuyerModalOpen(false);
    setSelectedBuyerId(null);
  };

  const handleBuyerClick = () => {
    if (currentBuyer?.id) {
      navigate(`/buyers/${currentBuyer.id}`);
    }
  };

  const renderBuyerDetails = (buyerEntity: BuyerEntitySearchOption) => (
    <div className="space-y-2">
      <h4 className="font-semibold text-foreground">{buyerEntity.name}</h4>
      {buyerEntity.metadata?.fullAddress && (
        <div className="flex items-start gap-2">
          <FiMapPin className="w-4 h-4 mt-0.5 text-default-500" />
          <p className="text-sm text-foreground-500">
            {buyerEntity.metadata.fullAddress}
          </p>
        </div>
      )}
      {buyerEntity.metadata?.referenceId && (
        <p className="text-sm text-foreground-500">
          ID: {buyerEntity.metadata.referenceId}
        </p>
      )}
    </div>
  );

  const renderUsersList = () => {
    if (usersLoading) {
      return <p className="text-sm text-foreground-500">Loading users...</p>;
    }

    if (buyerUsers.length === 0) {
      return (
        <p className="text-sm text-foreground-500">
          No users found for this buyer
        </p>
      );
    }

    return (
      <ul className="space-y-2">
        {buyerUsers.map((user) => (
          <li key={user.value} className="text-sm text-foreground-500">
            {user.label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FiTruck className="w-6 h-6 text-primary" />
          <div>
            <h3 className="text-xl font-semibold text-foreground">Buyer</h3>
            <p className="text-sm text-foreground-500">
              The buyer associated with this chat
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="flat"
          color="primary"
          startContent={<FiEdit className="w-4 h-4" />}
          onPress={() => setIsChangeBuyerModalOpen(true)}
        >
          Change Buyer
        </Button>
      </div>

      <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
        <p className="text-sm text-warning-800">
          <strong>Warning:</strong> If you change the chat buyer entity, all the
          users in the chat (buyer & supplier) will be immediately cleared and
          you will have to reintroduce them one-by-one.
        </p>
      </div>

      {currentBuyer ? (
        <div
          className="flex items-center justify-between p-4 bg-content2 rounded-lg cursor-pointer hover:bg-content3 transition-colors group"
          onClick={handleBuyerClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBuyerClick();
            }
          }}
        >
          <div className="flex-1">
            <p className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
              {currentBuyer.name}
            </p>
            {currentBuyer.referenceId && (
              <p className="text-sm text-foreground-500 mt-1">
                ID: {currentBuyer.referenceId}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Chip color="success" variant="flat" size="md">
              Active
            </Chip>
            <FiExternalLink className="w-4 h-4 text-foreground-400 group-hover:text-primary transition-colors" />
          </div>
        </div>
      ) : (
        <div className="p-4 bg-content2 rounded-lg">
          <p className="text-foreground-500 italic">No buyer selected</p>
        </div>
      )}

      <EntitySearchModal
        isOpen={isChangeBuyerModalOpen}
        onClose={handleCancel}
        title="Change Chat Buyer"
        searchPlaceholder="Search buyers by name or address..."
        entityType="buyer"
        isSearching={searchLoading}
        searchResults={buyers.map((buyerOption) => ({
          id: buyerOption.value,
          name: buyerOption.label,
          label: buyerOption.label,
          subtitle: buyerOption.metaData?.fullAddress || '',
          metadata: {
            fullAddress: buyerOption.metaData?.fullAddress || '',
            name: buyerOption.metaData?.name || '',
            referenceId: buyerOption.metaData?.referenceId || '',
          },
        }))}
        onSearch={searchBuyers}
        onConfirm={handleConfirm}
        isConfirming={isUpdating}
        onSelect={handleBuyerSelect}
        renderConfirmation={(selectedBuyer) => (
          <div className="space-y-4">
            <div className="p-4 bg-primary-50 rounded-lg">
              {renderBuyerDetails(selectedBuyer)}
            </div>

            <div className="p-4 bg-content2 rounded-lg space-y-2">
              <h4 className="font-semibold text-foreground">
                Users to be added
              </h4>
              {renderUsersList()}
            </div>

            <FormFooter
              saveText="Update Buyer"
              onSave={() => handleConfirm(selectedBuyer)}
              onCancel={handleCancel}
              isSaving={isUpdating}
              isSaveDisabled={!selectedBuyerId || usersLoading}
            />
          </div>
        )}
      />
    </div>
  );
};
